import { Box, Button, MenuItem, Select, InputLabel, TextField } from '@mui/material';
import { useState, useEffect } from 'react';
import useApi from 'hooks/useApi';

interface Props {
  onAddField: (field: { type: string; label: string; resourceType?: string }) => void;
  showFieldTypeSelector?: boolean;
  clientUID?: string;
  /** Restrict the offered field types; omit to offer all of them. */
  allowedTypes?: string[];
}

const DefaultForm = ({ onAddField, showFieldTypeSelector, clientUID, allowedTypes }: Props) => {
  const [inputType, setInputType] = useState('TEXT');
  const [label, setLabel] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [resourceTypes, setResourceTypes] = useState<Array<{ id: string; name: string }>>([]);
  const [loadingResourceTypes, setLoadingResourceTypes] = useState(false);
  const [hasLoadedResourceTypes, setHasLoadedResourceTypes] = useState(false);

  const { getResourceTypes } = useApi();

  // Load resource types when "resource" field type is selected, but only if not already loaded
  useEffect(() => {
    if (inputType === 'RESOURCE' && !hasLoadedResourceTypes) {
      loadResourceTypes();
    }
  }, [inputType, hasLoadedResourceTypes]);

  const loadResourceTypes = async () => {
    if (hasLoadedResourceTypes) return; // Prevent multiple calls
    
    setLoadingResourceTypes(true);
    try {
      const response = await getResourceTypes(clientUID, null);
      if (response?.success && Array.isArray(response.data)) {
        setResourceTypes(response.data);
      } else if (Array.isArray(response)) {
        setResourceTypes(response);
      } else {
        console.error('Invalid resource types response:', response);
        setResourceTypes([]);
      }
      setHasLoadedResourceTypes(true); // Mark as loaded
    } catch (error) {
      console.error('Failed to load resource types:', error);
      setResourceTypes([]);
      setHasLoadedResourceTypes(true); // Mark as loaded even on error
    } finally {
      setLoadingResourceTypes(false);
    }
  };

  const handleAdd = () => {
    if (!label.trim()) return;
    
    // For resource type, require resource type selection
    if (inputType === 'RESOURCE' && !resourceType.trim()) {
      return;
    }

    const fieldData: { type: string; label: string; resourceType?: string } = {
      type: inputType,
      label
    };

    if (inputType === 'RESOURCE') {
      fieldData.resourceType = resourceType;
    }

    onAddField(fieldData);
    setLabel('');
    setResourceType('');
    setInputType('TEXT');
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} p={2} width={300}>
      {showFieldTypeSelector && (
        <Box display="flex" alignItems="center" gap={1}>
          <InputLabel sx={{ minWidth: 80 }}>Field Type:</InputLabel>
          <Select value={inputType} onChange={(e) => setInputType(e.target.value)} fullWidth size="small">
            {[
              { value: 'TEXT', display: 'TEXT' },
              { value: 'NUMBER', display: 'NUMBER' },
              { value: 'FILE', display: 'FILE' },
              // { value: 'PASSWORD', display: 'PASSWORD' },
              { value: 'SELECT', display: 'DROPDOWN' },
              { value: 'CHECKBOX', display: 'CHECKBOX' },
              { value: 'RADIO', display: 'RADIO' },
              { value: 'CAMERA', display: 'CAMERA' },
              // { value: 'FORM', display: 'FORM' },
              { value: 'DATE', display: 'DATE' },
              { value: 'ESIGN', display: 'ESIGN' },
              // { value: 'TEXTARRAY', display: 'TEXTARRAY' },
              { value: 'SWITCH', display: 'TOGGLE' },
              { value: 'RESOURCE', display: 'RESOURCE' }
            ]
              .filter((type) => !allowedTypes || allowedTypes.includes(type.value))
              .map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.display}
                </MenuItem>
              ))}
          </Select>
        </Box>
      )}

      {showFieldTypeSelector && inputType === 'RESOURCE' && (
        <Box display="flex" alignItems="center" gap={1}>
          <InputLabel sx={{ minWidth: 80 }}>Resource Type:</InputLabel>  
          <Select 
            value={resourceType} 
            onChange={(e) => setResourceType(e.target.value)} 
            fullWidth 
            size="small"
            disabled={loadingResourceTypes}
            displayEmpty
          >
            <MenuItem value="" disabled>
              {loadingResourceTypes ? 'Loading...' : 'Select Resource Type'}
            </MenuItem>
            {resourceTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      <Box display="flex" alignItems="center" gap={1}>
        <InputLabel sx={{ minWidth: 80 }}>Label:</InputLabel>
        <TextField value={label} onChange={(e) => setLabel(e.target.value)} fullWidth size="small" />
      </Box>
      <Button 
        variant="contained" 
        onClick={handleAdd} 
        disabled={!label.trim() || (inputType === 'RESOURCE' && !resourceType.trim())}
      >
        Add Field
      </Button>
    </Box>
  );
};

export default DefaultForm;
